"use client";

import { useMemo, useState } from "react";
import styles from "./index.module.scss";
import { BsFilter } from "react-icons/bs";
import { useBridgeScannerFiltersStore } from "@/features/bridge-scanner/stores/filters.store";
import {
  BSFilterParamEnum,
  type Transaction,
} from "@/types/bridge-scanner.types";
import BlessButton from "@/components/layout/button/green-outline-btn";
import { BLOCKCHAINS, TEST_BLOCKCHAINS } from "@/constants/chain-details";
import { useBridgeScannerStore } from "@/features/bridge-scanner/stores/bridge-scanner.store";
import { useFilterApply } from "@/features/bridge-scanner/hooks/useFilterApply.hook";
import ClickAwayListener from "react-click-away-listener";
import { AnimatePresence } from "framer-motion";
import CountIndicator from "@/components/layout/count-indicator";
import useMediaQuery from "@/hooks/useMediaQuery.hook";
import { useParams } from "next/navigation";
import FilterSelector from "@/features/bridge-scanner/components/table/left-controls/filter-selector";
import SliderFilterMenu from "@/features/bridge-scanner/components/table/left-controls/slideup-filter-menu";
import type { Option } from "@/features/bridge-scanner/components/table/left-controls/filter-option";

export const TRANSFER_TYPE_OPTIONS: Option[] = [
  { label: "BLESS", value: "BLESS" },
  { label: "ERC20", value: "ERC20" },
  { label: "ERC721", value: "ERC721" },
  { label: "ERC1155", value: "ERC1155" },
  { label: "TXN", value: "TXN" },
  { label: "DSPNSR", value: "DSPNSR" },
];

// TODO Relocate this to its own module
const DesktopFilterPanel = ({ onClose, handleApply, handleReset }: any) => {
  const { queryParams, setQueryParams } = useBridgeScannerFiltersStore();
  const { network } = useParams() as { network: string };

  const chainOptions = useMemo(
    () =>
      (network === "mainnet" ? BLOCKCHAINS : TEST_BLOCKCHAINS).map((i) => ({
        icon: i.logo,
        label: i.chainName,
        value: i.chainId.toString(),
      })),
    [network],
  );
  return (
    <div className={styles.filterPanelWrapper}>
      <ClickAwayListener onClickAway={onClose}>
        <div className={styles.filterPanel}>
          <div className={styles.filterWindows}>
            <FilterSelector
              header="Transfer Type"
              filterType={BSFilterParamEnum.TransferType}
              options={TRANSFER_TYPE_OPTIONS}
              selectedOptions={queryParams.get(BSFilterParamEnum.TransferType)}
              selectOption={setQueryParams}
            />
            <FilterSelector
              header="Source Chain"
              filterType={BSFilterParamEnum.SourceChain}
              options={chainOptions}
              selectedOptions={queryParams.get(BSFilterParamEnum.SourceChain)}
              selectOption={setQueryParams}
            />
            <FilterSelector
              header="Target Chain"
              filterType={BSFilterParamEnum.TargetChain}
              options={chainOptions}
              selectedOptions={queryParams.get(BSFilterParamEnum.TargetChain)}
              selectOption={setQueryParams}
            />
          </div>
          <div className={styles.filterPanelButtons}>
            <button className={styles.resetFilters} onClick={handleReset}>
              Reset Filters
            </button>
            <BlessButton onClick={handleApply}>Apply</BlessButton>
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};

type FilterProps = {
  resetTableIndex: () => void;
};

const Filter = ({ resetTableIndex }: FilterProps) => {
  const { handleApplyParams } = useFilterApply();
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery(parseInt(styles.breakpointSmall));
  const { queryParams, resetQueryParams } = useBridgeScannerFiltersStore(
    (store) => ({
      queryParams: store.queryParams,
      resetQueryParams: store.resetQueryParams,
    }),
  );
  const { isControlWindowOpen, setControlWindowOpen } = useBridgeScannerStore();

  const toggleFilterWindow = () => {
    if (isDesktop) document.body.style.overflow = "unset";
    else document.body.style.overflow = isOpen ? "unset" : "hidden"; // Disable scrolling
    setIsOpen((prev) => !prev);
    setControlWindowOpen(!isControlWindowOpen);
  };

  const closeFilterWindow = (event?: any) => {
    if ((event?.target as HTMLElement)?.closest(`#filterButton`)) return;
    setIsOpen(false);
    setControlWindowOpen(false);
    document.body.style.overflow = "unset";
  };

  const handleApply = () => {
    setIsOpen(false);
    setControlWindowOpen(false);
    handleApplyParams();
    resetTableIndex();
    document.body.style.overflow = "unset"; // Enable scrolling again
  };

  const totalActiveParams = useMemo(
    () => Array.from(queryParams.values()).flatMap((i) => i).length,
    [queryParams],
  );

  return (
    <div className={styles.popUpWrapper}>
      <button
        className={styles.leftControlBtn}
        onClick={toggleFilterWindow}
        id="filterButton"
      >
        <BsFilter size={25} /> Filters{" "}
        <CountIndicator count={totalActiveParams} />
      </button>

      <AnimatePresence>
        {isOpen &&
          (isDesktop ? (
            <DesktopFilterPanel
              onClose={closeFilterWindow}
              handleApply={handleApply}
              handleReset={resetQueryParams}
            />
          ) : (
            <SliderFilterMenu onClose={closeFilterWindow} />
          ))}
      </AnimatePresence>
    </div>
  );
};

export default Filter;
